import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Fingerprint, User, Lock, Eye, EyeOff, ShieldCheck, Mail,
  KeyRound, ArrowLeft, Loader2, ShieldAlert, CheckCircle2,
  Shield, AlertTriangle, Clock, Smartphone, Monitor, Zap,
  Activity, RefreshCw, X,
} from 'lucide-react';
import { userFromServer, type StoredUser } from './authUser';

// ══════════════════════════════════════════════════════════════
//  JAISWAL CAPITAL — SMART LOGIN SYSTEM V3.1 (ULTIMATE FIX)
//  ✅ FIXED: getModifierState Crash (Caps Lock Check)
//  Real Auth from UserControl | 2FA | Rate Limiting | Logs
//  Author: Subhas Prasad
// ══════════════════════════════════════════════════════════════

interface LoginProps {
  onLoginSuccess: (role: 'ADMIN' | 'STAFF' | 'CLIENT', id: string) => void;
}


const _TICKER_API = 'https://api.jaiswalcapital.com';
const _TICKER_SYMS = ['NIFTY_50', 'BANK_NIFTY', 'FIN_NIFTY', 'SENSEX', 'MIDCPNIFTY'];

/* ─── AMBIENT PARTICLES ─── */
const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 3 + 1,
  dur: Math.random() * 8 + 6,
  del: Math.random() * 5,
}));

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  /* ─── View ─── */
  const [view, setView] = useState<'LOGIN' | 'FORGOT' | 'OTP' | 'LOCKED' | 'CHANGE_PWD'>('LOGIN');
  // 🔐 change-password (backend /api/auth/change-password)
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew,     setCpNew]     = useState('');
  const [cpConfirm, setCpConfirm] = useState('');

  /* ─── Form ─── */
  const [userId,   setUserId]   = useState('');
  const [password, setPassword] = useState('');
  const [otp,      setOtp]      = useState(['', '', '', '', '', '']);
  const [showPwd,  setShowPwd]  = useState(false);
  const [resetId,  setResetId]  = useState('');

  /* ─── UI ─── */
  const [isLoading,  setIsLoading]  = useState(false);
  const [errorMsg,   setErrorMsg]   = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [attempts,   setAttempts]   = useState(0);
  const [lockTime,   setLockTime]   = useState(0);
  const [currentBg,  setCurrentBg]  = useState(0);
  const [tickerPos,  setTickerPos]  = useState(0);
  const [time,       setTime]       = useState(new Date());
  const [matchedUser,setMatchedUser]= useState<StoredUser | null>(null);
  const [capsLock,   setCapsLock]   = useState(false);
  const [showWelcome,setShowWelcome]= useState(false);
  const [tickers,    setTickers]    = useState<{sym:string;val:string;chg:string;up:boolean}[]>([]);

  /* ─── Refs ─── */
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const pwdRef  = useRef<HTMLInputElement>(null);

  /* ─── BG images ─── */
  const BG_IMAGES = ['/bg1.jpg', '/bg2.jpg', '/bg3.jpg', '/bg4.jpg'];

  /* ─── Effects ─── */
  useEffect(() => {
    const t = setInterval(() => setCurrentBg(p => (p + 1) % BG_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fetchTickers = async () => {
      try {
        const results = await Promise.allSettled(
          _TICKER_SYMS.map(s =>
            fetch(`${_TICKER_API}/quote?symbol=${s}`, { signal: AbortSignal.timeout(5000) }).then(r => r.json())
          )
        );
        const tks = results
          .map((r, i) => {
            if (r.status !== 'fulfilled') return null;
            const d = r.value;
            // Accept both live (success) and yfinance fallback (success with source) and OFFLINE with stale_ltp
            const price: number = d.ltp > 0 ? d.ltp : (d.stale_ltp ?? 0);
            if (price <= 0) return null;
            const pct: number = d.changePct ?? 0;
            const isStale = d.status !== 'success' || d.source === 'yfinance_close';
            return {
              sym: _TICKER_SYMS[i].replace(/_/g, ' '),
              val: Number(price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              chg: isStale && pct === 0 ? 'PREV CLOSE' : `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
              up: pct >= 0,
            };
          })
          .filter(Boolean) as { sym: string; val: string; chg: string; up: boolean }[];
        if (tks.length > 0) setTickers(tks);
      } catch { /* silently ignore — login page, non-critical */ }
    };
    fetchTickers();
    const poll = setInterval(fetchTickers, 30000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    const ticker_w = tickers.length * 160;
    if (ticker_w === 0) return;
    const t = setInterval(() => setTickerPos(p => p <= -ticker_w ? 0 : p - 0.5), 20);
    return () => clearInterval(t);
  }, [tickers.length]);

  useEffect(() => {
    if (lockTime > 0) {
      const t = setTimeout(() => setLockTime(s => s - 1), 1000);
      return () => clearTimeout(t);
    } else if (lockTime === 0 && view === 'LOCKED') {
      setView('LOGIN'); setAttempts(0);
    }
  }, [lockTime, view]);

  // ✅ FIXED: Safe CapsLock check to prevent 'getModifierState is not a function' error
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e && typeof e.getModifierState === 'function') {
        setCapsLock(e.getModifierState('CapsLock'));
      } else {
        setCapsLock(false);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  /* ─── Load users from localStorage ─── */
  const getStoredUsers = useCallback((): StoredUser[] => {
    // Master user profiles — passwords verified server-side only (v18 Bug 2.4)
    const MASTER_USERS: StoredUser[] = [
      // NAME is the display name and is now spelled correctly (God 2026-09-04:
      // "Subhas", no trailing 'h'). The `id` values are LOGIN IDENTIFIERS, not
      // labels, so the correction is ADDITIVE: 'SUBHAS' is now accepted, and the
      // old 'SUBHASH' is deliberately KEPT ACTIVE as a fallback (God 2026-09-04:
      // "keep the old one active so nothing breaks"). Deleting a login id is how
      // an admin gets locked out of his own console; adding one costs nothing.
      { id:'ADMIN',                     name:'Subhas Prasad', role:'MASTER_ADMIN', status:'ACTIVE', password:'', twoFA:false, permissions:{}, department:'PROP TRADING DESK' },
      { id:'SUBHAS',                    name:'Subhas Prasad', role:'MASTER_ADMIN', status:'ACTIVE', password:'', twoFA:false, permissions:{}, department:'PROP TRADING DESK' },
      { id:'SUBHASH',                   name:'Subhas Prasad', role:'MASTER_ADMIN', status:'ACTIVE', password:'', twoFA:false, permissions:{}, department:'PROP TRADING DESK' },
      { id:'admin@jaiswalcapital.com',  name:'Subhas Prasad', role:'MASTER_ADMIN', status:'ACTIVE', password:'', twoFA:false, permissions:{}, department:'PROP TRADING DESK' },
      { id:'MAMTA',                     name:'Subhas Prasad', role:'MASTER_ADMIN', status:'ACTIVE', password:'', twoFA:false, permissions:{}, department:'PROP TRADING DESK' },
      { id:'staff1@jaiswalcapital.com', name:'System Operator', role:'TRADER',       status:'ACTIVE', password:'', twoFA:false, permissions:{}, department:'TRADING DESK' },
    ];
    try {
      const saved = localStorage.getItem('jaiswal_system_users');
      if (saved) {
        const parsed = JSON.parse(saved) as StoredUser[];
        if (parsed.length > 0) {
          // Merge: master users + saved users (saved users take priority)
          const savedIds = parsed.map((u: StoredUser) => u.id.toLowerCase());
          const uniqueMasters = MASTER_USERS.filter(m => !savedIds.includes(m.id.toLowerCase()));
          return [...uniqueMasters, ...parsed];
        }
      }
    } catch { /* ignore */ }
    return MASTER_USERS;
  }, []);

  /* ─── Save activity log ─── */
  const logActivity = useCallback((userId: string, action: string, detail: string, type: string) => {
    try {
      const prev = JSON.parse(localStorage.getItem('jaiswal_activity_logs') || '[]');
      const entry = { id: 'L' + Date.now(), userId, action, module: 'Auth', detail, time: new Date().toLocaleString('en-IN'), type, ip: '103.21.xx.xx' };
      localStorage.setItem('jaiswal_activity_logs', JSON.stringify([entry, ...prev].slice(0, 200)));
    } catch { /* ignore */ }
    // Phase 3: mirror to the server-side audit trail (persistent, cross-device).
    fetch('https://api.jaiswalcapital.com/admin/audit', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('jc_auth_token') || '') },
      body: JSON.stringify({ userId, action, module: 'Auth', detail, type }),
    }).catch(() => {});
  }, []);

  /* ─── Handle Login ─── */
  const handleLogin = useCallback(async () => {
    setErrorMsg('');
    if (!userId.trim())   { setErrorMsg('System ID is required.'); return; }
    if (!password.trim()) { setErrorMsg('Access Key is required.'); return; }
    if (lockTime > 0) return;

    setIsLoading(true);
    // The server decides who exists (/api/auth/login). The old pre-fetch of
    // /admin/users is 401 without a session since the multi-tenant change
    // (2026-09-26), so on a fresh device a staff user created in Master Control
    // was rejected here as "Invalid ID" before the password ever reached the
    // server. The local list only lends a display name / cached status.
    const typedId = userId.trim();
    const match = getStoredUsers().find(u => u.id.toLowerCase() === typedId.toLowerCase());

    if (match && match.status === 'BLOCKED') {
      setIsLoading(false);
      setErrorMsg('Account is BLOCKED. Contact Master Admin.');
      logActivity(userId, 'Login Blocked', 'Account blocked by admin', 'ERROR');
      return;
    }
    if (match && match.status === 'INACTIVE') {
      setIsLoading(false);
      setErrorMsg('Account is INACTIVE. Contact admin to activate.');
      return;
    }

    try {
      const res = await fetch('https://api.jaiswalcapital.com/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: match ? match.id : typedId, password })
      });
      setIsLoading(false);

      if (res.status >= 500) {
        setErrorMsg('Authentication service unavailable. Please try again in a moment.');
        logActivity(userId, 'Login Error', `Server ${res.status}`, 'ERROR');
        return;
      }

      const data = await res.json();
      const pwdOk = res.ok && data.valid === true;

      if (!pwdOk) {
        // server-side lockout message ko priority (bypass-proof brute-force guard)
        if (data.locked) { setErrorMsg(data.message || 'Locked — try later'); setView('LOCKED'); setLockTime(60); return; }
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= 5) { setLockTime(30); setView('LOCKED'); return; }
        // One message for a wrong id and a wrong password: the screen must not tell
        // a stranger which System IDs exist (the server says "Unknown user").
        setErrorMsg(`Invalid credentials. ${5 - newAttempts} attempt(s) remaining.`);
        logActivity(userId, 'Login Failed', 'Wrong password', 'ERROR');
        return;
      }

      const user = userFromServer(match, typedId, data);
      setMatchedUser(user);
      setAttempts(0);
      // Security: store the bearer auth token issued on login — sent as X-Session-Token
      // on every admin write so those endpoints are no longer open to the internet.
      if (data.authToken) { try { localStorage.setItem('jc_auth_token', data.authToken); } catch {} }
      // PROFILE-ON-LOGIN (God 2026-08-30): this device's own server-truth
      // record — permissions/avatar/usage — powers page enforcement + header
      // identity on EVERY device from the same store.
      if (data.profile && data.profile.id) { try { localStorage.setItem('jc_my_profile', JSON.stringify(data.profile)); } catch {} }

      // OTP is enforced SERVER-side: on a valid password the server generates the
      // OTP and pushes it to the admin's Telegram, then returns otpRequired:true.
      // (Lockout-safe — if delivery is unavailable the server returns
      // otpRequired:false so the admin is never trapped.) No client twoFA flag.
      if (data.otpRequired === true) {
        setView('OTP');
        setSuccessMsg(`🔐 OTP sent to ${data.otpDest || 'your registered email'}. Enter the 6-digit code.`);
        logActivity(user.id, 'OTP Sent', `2FA to ${data.otpDest || 'email'}`, 'LOGIN');
      } else {
        if (data.otpWarning) logActivity(user.id, 'OTP Skipped', String(data.otpWarning), 'ERROR');
        doLoginSuccess(user);
      }
    } catch {
      setIsLoading(false);
      setErrorMsg('Authentication service unavailable. Try again.');
    }
  }, [userId, password, attempts, lockTime, getStoredUsers, logActivity]);

  /* ─── OTP ─── */
  const handleOtpChange = (i: number, val: string) => {
    if (!/^[0-9]?$/.test(val)) return;
    const n = [...otp]; n[i] = val;
    setOtp(n);
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const handleOtpKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const verifyOtp = async () => {
    const code = otp.join('');
    if (code.length < 6) { setErrorMsg('Enter complete 6-digit OTP.'); return; }
    if (!matchedUser) { setErrorMsg('Session expired. Please login again.'); setView('LOGIN'); return; }
    setIsLoading(true);
    try {
      const r = await fetch('https://api.jaiswalcapital.com/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: matchedUser.id, otp: code })
      });
      const result = await r.json();
      setIsLoading(false);
      if (r.ok && result.valid === true) {
        // authToken is issued only AFTER OTP verify (2FA) — store it before proceeding.
        if (result.authToken) { try { localStorage.setItem('jc_auth_token', result.authToken); } catch {} }
        if (result.profile && result.profile.id) { try { localStorage.setItem('jc_my_profile', JSON.stringify(result.profile)); } catch {} }
        doLoginSuccess(userFromServer(matchedUser, matchedUser.id, result));
      } else {
        setErrorMsg(result.message || 'Invalid OTP. Try again.');
      }
    } catch {
      setIsLoading(false);
      setErrorMsg('OTP service unavailable. Try again.');
    }
  };

  /* ─── Do login success ─── */
  const doLoginSuccess = (user: StoredUser) => {
    setShowWelcome(true);
    logActivity(user.id, 'Login Success', `Role: ${user.role}`, 'LOGIN');
    // Phase 3: register a REAL server-side session (post-OTP) + persistent audit.
    fetch('https://api.jaiswalcapital.com/admin/sessions/start', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('jc_auth_token') || '') },
      body: JSON.stringify({ userId: user.id, role: user.role, name: user.name }),
    }).then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.sessionId) localStorage.setItem('jc_session_id', d.sessionId); })
      .catch(() => {});
    // Save current session
    try {
      const prev = JSON.parse(localStorage.getItem('jaiswal_system_users') || '[]') as StoredUser[];
      const updated = prev.map(u => u.id === user.id ? { ...u, lastLogin: new Date().toLocaleString('en-IN'), loginCount: ((u as unknown as Record<string, number>).loginCount || 0) + 1 } : u);
      localStorage.setItem('jaiswal_system_users', JSON.stringify(updated));
    } catch { /* ignore */ }
    setTimeout(() => {
      const role = user.role === 'MASTER_ADMIN' || user.role === 'ADMIN' ? 'ADMIN' : user.role === 'CLIENT' ? 'CLIENT' : 'STAFF';
      onLoginSuccess(role as 'ADMIN' | 'STAFF' | 'CLIENT', user.id);
    }, 2000);
  };

  /* ─── Forgot password ─── */
  const handleReset = () => {
    if (!resetId.trim()) { setErrorMsg('Enter your System ID.'); return; }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setSuccessMsg('Password reset link transmitted to registered contact.');
      logActivity(resetId, 'Password Reset Requested', 'Via forgot password', 'EDIT');
      setTimeout(() => { setSuccessMsg(''); setView('LOGIN'); }, 4000);
    }, 1500);
  };

  const clearError = () => setErrorMsg('');

  /* ─── IST time ─── */
  const timeStr = time.toLocaleTimeString('en-IN', { hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = time.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

  /* ─── Market status ─── */
  const mktOpen = (() => { const h = time.getHours(), m = time.getMinutes(); return (h === 9 && m >= 15) || (h > 9 && h < 15) || (h === 15 && m <= 30); })();

  /* ════════════════════════════════════════════════════ RENDER ════════════════════════════════════════════════════ */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', width: '100%', background: '#020202', fontFamily: "'Segoe UI',system-ui,sans-serif", overflow: 'hidden', position: 'relative' }}>
      <style>{`
        @keyframes lg-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
        @keyframes lg-pulse{0%,100%{opacity:.3}50%{opacity:.7}}
        @keyframes lg-spin{to{transform:rotate(360deg)}}
        @keyframes lg-fade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes lg-slide{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
        @keyframes lg-glow{0%,100%{box-shadow:0 0 20px rgba(245,158,11,.2)}50%{box-shadow:0 0 40px rgba(245,158,11,.5)}}
        @keyframes lg-scan{0%{transform:translateY(-100%)}100%{transform:translateY(800%)}}
        @keyframes lg-welcome{0%{opacity:0;transform:scale(.9)}50%{opacity:1;transform:scale(1.02)}100%{opacity:1;transform:scale(1)}}
        @keyframes lg-ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        .lg-inp{background:rgba(0,0,0,.6);border:1px solid rgba(255,255,255,.08);border-radius:12px;padding:14px 14px 14px 46px;color:#fff;font-size:13px;font-weight:700;outline:none;width:100%;transition:all .25s;letter-spacing:.5px}
        .lg-inp:focus{border-color:rgba(245,158,11,.5);box-shadow:0 0 0 1px rgba(245,158,11,.3),inset 0 0 20px rgba(245,158,11,.04)}
        .lg-inp::placeholder{color:rgba(255,255,255,.15);font-size:12px}
        .lg-btn{width:100%;padding:15px;border:none;border-radius:12px;font-size:11px;font-weight:900;letter-spacing:2px;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .3s}
        .lg-btn-gold{background:linear-gradient(135deg,#d97706,#f59e0b,#fbbf24);color:#000;box-shadow:0 8px 24px rgba(245,158,11,.25)}
        .lg-btn-gold:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 12px 36px rgba(245,158,11,.4)}
        .lg-btn-gold:disabled{opacity:.6;cursor:not-allowed;transform:none}
        .lg-otp-inp{width:46px;height:54px;background:rgba(0,0,0,.5);border:1px solid rgba(255,255,255,.1);border-radius:10px;text-align:center;font-size:20px;font-weight:900;color:#f59e0b;outline:none;transition:all .2s;font-family:monospace}
        .lg-otp-inp:focus{border-color:rgba(245,158,11,.6);box-shadow:0 0 0 1px rgba(245,158,11,.3);background:rgba(245,158,11,.05)}
        .lg-card{padding:36px 32px}
        .lg-otp-row{gap:8px}
        @media(max-width:480px){
          .lg-card{padding:22px 16px!important;border-radius:20px!important}
          .lg-otp-inp{width:36px!important;height:46px!important;font-size:16px!important}
          .lg-otp-row{gap:6px!important}
          .lg-topbar-date{display:none!important}
          .lg-bottom-links{display:none!important}
        }
      `}</style>

      {/* ─── BACKGROUND ─── */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
        {BG_IMAGES.map((bg, i) => (
          <div key={i} style={{ position: 'absolute', inset: 0, backgroundImage: `url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: i === currentBg ? 0.22 : 0, transition: 'opacity 1.5s ease, transform 6s ease', transform: i === currentBg ? 'scale(1.05)' : 'scale(1)' }}/>
        ))}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom,rgba(2,2,2,.85) 0%,rgba(2,2,2,.4) 50%,rgba(2,2,2,.9) 100%)' }}/>
        <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(8px)' }}/>
        {/* Ambient orbs */}
        <div style={{ position: 'absolute', top: '20%', left: '15%', width: 400, height: 400, background: 'radial-gradient(circle,rgba(245,158,11,.08) 0%,transparent 70%)', borderRadius: '50%', animation: 'lg-pulse 6s ease infinite' }}/>
        <div style={{ position: 'absolute', bottom: '20%', right: '15%', width: 350, height: 350, background: 'radial-gradient(circle,rgba(124,58,237,.06) 0%,transparent 70%)', borderRadius: '50%', animation: 'lg-pulse 8s ease infinite 2s' }}/>
        {/* Floating particles */}
        {PARTICLES.map(p => (
          <div key={p.id} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, borderRadius: '50%', background: 'rgba(245,158,11,.4)', animation: `lg-float ${p.dur}s ease infinite ${p.del}s` }}/>
        ))}
        {/* Scan line */}
        <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,rgba(245,158,11,.15),transparent)', animation: 'lg-scan 8s linear infinite' }}/>
      </div>

      {/* ─── TOP BAR ─── */}
      <div style={{ position: 'relative', zIndex: 10, padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,.04)', background: 'rgba(0,0,0,.3)', backdropFilter: 'blur(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, background: 'linear-gradient(135deg,#d97706,#f59e0b)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>⚡</div>
          <span style={{ fontSize: 11, fontWeight: 900, color: '#f59e0b', letterSpacing: 3, textTransform: 'uppercase' }}>Jaiswal Capital</span>
          <span style={{ fontSize: 8, fontWeight: 700, color: '#374151', letterSpacing: 2, textTransform: 'uppercase', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.06)', padding: '2px 8px', borderRadius: 4 }}>Restricted System</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: '#fff', fontFamily: 'monospace', letterSpacing: 1 }}>{timeStr}</div>
            <div className="lg-topbar-date" style={{ fontSize: 8, color: '#4b5563', letterSpacing: 1.5, textTransform: 'uppercase' }}>{dateStr}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 6, background: mktOpen ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', border: `1px solid ${mktOpen ? 'rgba(16,185,129,.3)' : 'rgba(239,68,68,.3)'}` }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: mktOpen ? '#10b981' : '#ef4444', animation: mktOpen ? 'lg-pulse 1.5s infinite' : 'none' }}/>
            <span style={{ fontSize: 8, fontWeight: 800, color: mktOpen ? '#10b981' : '#f87171', letterSpacing: 1.5, textTransform: 'uppercase' }}>{mktOpen ? 'Market Open' : 'Market Closed'}</span>
          </div>
        </div>
      </div>

      {/* ─── LIVE TICKER ─── */}
      <div style={{ position: 'relative', zIndex: 10, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,.04)', padding: '6px 0', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 0, width: 'max-content', animation: 'none', transform: `translateX(${tickerPos}px)`, transition: 'none' }}>
          {(tickers.length > 0 ? [...tickers, ...tickers, ...tickers] : []).map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 28px', borderRight: '1px solid rgba(255,255,255,.05)', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 8.5, fontWeight: 700, color: '#4b5563', letterSpacing: 1.5, textTransform: 'uppercase' }}>{t.sym}</span>
              <span style={{ fontSize: 11, fontWeight: 900, color: '#e2e8f0', fontFamily: 'monospace' }}>{t.val}</span>
              <span style={{ fontSize: 9, fontWeight: 800, color: t.up ? '#10b981' : '#ef4444', fontFamily: 'monospace' }}>{t.chg}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── MAIN BODY ─── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 10, padding: '24px 16px' }}>

        {/* LEFT — System stats (desktop) */}
        <div style={{ display: 'none', flexDirection: 'column', gap: 12, marginRight: 48, maxWidth: 220 }} className="lg-left">
          {[
            { l: 'System Version', v: 'V9.5 STABLE',      col: '#10b981' },
            { l: 'AI Engine',      v: 'MAMTA PRO ACTIVE', col: '#818cf8' },
            { l: 'Security',       v: 'AES-256 ENCRYPTED',col: '#fbbf24' },
            { l: 'Uptime',         v: '99.97%',           col: '#38bdf8' },
          ].map(s => (
            <div key={s.l} style={{ background: 'rgba(0,0,0,.4)', border: '1px solid rgba(255,255,255,.05)', borderRadius: 10, padding: '10px 14px', backdropFilter: 'blur(10px)' }}>
              <div style={{ fontSize: 8, color: '#374151', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 3 }}>{s.l}</div>
              <div style={{ fontSize: 10.5, fontWeight: 900, color: s.col, letterSpacing: 1 }}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* ─── LOGIN CARD ─── */}
        <div className="lg-card" style={{ width: '100%', maxWidth: 400, background: 'rgba(5,5,5,.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 28, boxShadow: '0 0 80px rgba(0,0,0,.8), inset 0 0 80px rgba(245,158,11,.01)', position: 'relative', overflow: 'hidden', animation: 'lg-fade .5s ease' }}>
          {/* Card top accent */}
          <div style={{ position: 'absolute', top: 0, left: '25%', right: '25%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(245,158,11,.5),transparent)' }}/>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 60, background: 'linear-gradient(to bottom,rgba(245,158,11,.03),transparent)', pointerEvents: 'none' }}/>

          {/* ── WELCOME OVERLAY ── */}
          {showWelcome && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.92)', zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 28, animation: 'lg-welcome .5s ease' }}>
              <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'linear-gradient(135deg,rgba(16,185,129,.2),rgba(16,185,129,.05))', border: '2px solid rgba(16,185,129,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: '0 0 40px rgba(16,185,129,.3)' }}>
                <CheckCircle2 size={32} style={{ color: '#10b981' }}/>
              </div>
              <div style={{ fontSize: 14, fontWeight: 900, color: '#fff', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Access Granted</div>
              <div style={{ fontSize: 10, color: '#10b981', letterSpacing: 1.5, textTransform: 'uppercase' }}>Redirecting to system...</div>
              <div style={{ marginTop: 20, width: 200, height: 3, background: '#1a1a1a', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg,#059669,#10b981)', borderRadius: 3, animation: 'lg-scan 2s linear forwards', width: '100%' }}/>
              </div>
            </div>
          )}

          {/* ── LOGO ── */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
            <div style={{ width: 76, height: 76, background: 'rgba(10,10,10,.8)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, position: 'relative', animation: 'lg-glow 3s ease infinite', overflow: 'hidden' }}>
              <img src="/logo.png" alt="Logo" style={{ width: '80%', height: '80%', objectFit: 'contain', position: 'relative', zIndex: 1 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.setAttribute('style', 'display:flex'); }}/>
              <div style={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', fontSize: 28, display: 'none' }}>⚡</div>
              {isLoading && <div style={{ position: 'absolute', inset: 0, border: '2px solid transparent', borderTopColor: '#f59e0b', borderRadius: 20, animation: 'lg-spin 1s linear infinite' }}/>}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', letterSpacing: 3, textTransform: 'uppercase', textAlign: 'center', marginBottom: 8 }}>
              JAISWAL <span style={{ color: '#f59e0b' }}>CAPITAL</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)', padding: '5px 12px', borderRadius: 6 }}>
              <ShieldAlert size={11} style={{ color: '#f87171' }}/>
              <span style={{ fontSize: 8.5, fontWeight: 800, color: '#f87171', letterSpacing: 2, textTransform: 'uppercase' }}>Restricted System Access</span>
            </div>
          </div>

          {/* ── ERROR / SUCCESS ── */}
          {errorMsg && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8, animation: 'lg-slide .3s ease' }}>
              <AlertTriangle size={14} style={{ color: '#f87171', flexShrink: 0 }}/>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: '#f87171', flex: 1 }}>{errorMsg}</span>
              <button onClick={clearError} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2 }}><X size={12}/></button>
            </div>
          )}
          {successMsg && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.25)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8, animation: 'lg-slide .3s ease' }}>
              <CheckCircle2 size={14} style={{ color: '#10b981', flexShrink: 0 }}/>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: '#10b981' }}>{successMsg}</span>
            </div>
          )}

          {/* ── LOCKED VIEW ── */}
          {view === 'LOCKED' && (
            <div style={{ textAlign: 'center', animation: 'lg-fade .4s ease' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(239,68,68,.1)', border: '2px solid rgba(239,68,68,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Lock size={26} style={{ color: '#f87171' }}/>
              </div>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#f87171', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Account Locked</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 20, lineHeight: 1.6 }}>Too many failed attempts. System locked for security.</div>
              <div style={{ fontSize: 32, fontWeight: 900, fontFamily: 'monospace', color: '#f87171', marginBottom: 8 }}>{lockTime}s</div>
              <div style={{ fontSize: 9, color: '#374151', letterSpacing: 1.5, textTransform: 'uppercase' }}>Auto-unlock in {lockTime} seconds</div>
            </div>
          )}

          {/* ── LOGIN VIEW ── */}
          {view === 'LOGIN' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, animation: 'lg-fade .4s ease' }}>
              {/* User ID */}
              <div>
                <div style={{ fontSize: 8.5, fontWeight: 700, color: '#4b5563', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>System Authorized ID</div>
                <div style={{ position: 'relative' }}>
                  <User size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b5563' }}/>
                  <input type="text" value={userId} onChange={e => { setUserId(e.target.value); clearError(); }} onKeyDown={e => e.key === 'Enter' && pwdRef.current?.focus()} placeholder="Enter Master ID / Email" className="lg-inp" autoComplete="username"/>
                </div>
              </div>

              {/* Password */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 8.5, fontWeight: 700, color: '#4b5563', letterSpacing: 2, textTransform: 'uppercase' }}>Secure Access Key</div>
                  <button onClick={() => { setView('FORGOT'); setErrorMsg(''); }} style={{ fontSize: 8, fontWeight: 800, color: '#374151', background: 'transparent', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1.5, transition: 'color .2s' }} onMouseEnter={e => (e.currentTarget.style.color = '#f59e0b')} onMouseLeave={e => (e.currentTarget.style.color = '#374151')}>
                    Override Key?
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b5563' }}/>
                  <input ref={pwdRef} type={showPwd ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); clearError(); }} onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="••••••••" className="lg-inp" style={{ paddingRight: 44, letterSpacing: 2 }} autoComplete="current-password"/>
                  <button onClick={() => setShowPwd(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#4b5563', padding: 4 }}>
                    {showPwd ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
                {capsLock && <div style={{ fontSize: 8.5, color: '#fbbf24', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={10}/>Caps Lock is ON</div>}
              </div>

              {/* Attempts indicator */}
              {attempts > 0 && (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < attempts ? '#ef4444' : 'rgba(255,255,255,.08)', transition: 'background .3s' }}/>
                  ))}
                  <span style={{ fontSize: 8, color: '#ef4444', fontWeight: 700, marginLeft: 4, whiteSpace: 'nowrap' }}>{attempts}/5</span>
                </div>
              )}

              {/* Login button */}
              <button onClick={handleLogin} disabled={isLoading || lockTime > 0} className="lg-btn lg-btn-gold" style={{ marginTop: 4 }}>
                {isLoading ? <Loader2 size={16} style={{ animation: 'lg-spin 1s linear infinite' }}/> : <Fingerprint size={16}/>}
                {isLoading ? 'Verifying Credentials...' : 'Authenticate Access'}
              </button>

              {/* 🔐 Change password */}
              <button onClick={() => { setView('CHANGE_PWD'); setErrorMsg(''); setSuccessMsg(''); }} style={{ fontSize: 8.5, fontWeight: 800, color: '#374151', background: 'transparent', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1.5, padding: 6, transition: 'color .2s' }} onMouseEnter={e => (e.currentTarget.style.color = '#f59e0b')} onMouseLeave={e => (e.currentTarget.style.color = '#374151')}>
                🔐 Change Access Key
              </button>

            </div>
          )}

          {/* ── 🔐 CHANGE PASSWORD VIEW ── */}
          {view === 'CHANGE_PWD' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, animation: 'lg-slide .4s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: 2 }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <KeyRound size={22} style={{ color: '#f59e0b' }}/>
                </div>
                <div style={{ fontSize: 13, fontWeight: 900, color: '#fff', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}>Change Access Key</div>
                <div style={{ fontSize: 10, color: '#4b5563', lineHeight: 1.6 }}>Current key verify hogi, naya key turant lagu (server-side).</div>
              </div>
              {[
                { ph: 'Current Access Key', v: cpCurrent, fn: setCpCurrent },
                { ph: 'New Access Key (min 8, mix)', v: cpNew, fn: setCpNew },
                { ph: 'Confirm New Access Key', v: cpConfirm, fn: setCpConfirm },
              ].map(f => (
                <div key={f.ph} style={{ position: 'relative' }}>
                  <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b5563' }}/>
                  <input type="password" value={f.v} onChange={e => { f.fn(e.target.value); setErrorMsg(''); }} placeholder={f.ph} className="lg-inp" autoComplete="new-password"/>
                </div>
              ))}
              {cpNew && (
                <div style={{ display: 'flex', gap: 3 }}>
                  {[1,2,3,4].map(i => {
                    let sc = 0; if (cpNew.length >= 8) sc++; if (/[a-z]/.test(cpNew) && /[A-Z]/.test(cpNew)) sc++; if (/\d/.test(cpNew)) sc++; if (/[^A-Za-z0-9]/.test(cpNew)) sc++;
                    return <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: sc >= i ? (sc <= 1 ? '#ef4444' : sc === 2 ? '#fbbf24' : '#10b981') : 'rgba(255,255,255,.08)' }}/>;
                  })}
                </div>
              )}
              <button disabled={isLoading} className="lg-btn lg-btn-gold" onClick={async () => {
                if (!cpCurrent || !cpNew) { setErrorMsg('Dono keys bharo.'); return; }
                if (cpNew !== cpConfirm) { setErrorMsg('New key confirm se match nahi karti.'); return; }
                setIsLoading(true);
                try {
                  const r = await fetch('https://api.jaiswalcapital.com/api/auth/change-password', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                    body: JSON.stringify({ currentPassword: cpCurrent, newPassword: cpNew }),
                  });
                  const d = await r.json();
                  setIsLoading(false);
                  if (r.ok && d.status === 'success') {
                    setSuccessMsg('✅ ' + (d.message || 'Password changed'));
                    setCpCurrent(''); setCpNew(''); setCpConfirm('');
                    setTimeout(() => { setView('LOGIN'); setSuccessMsg(''); }, 2500);
                  } else { setErrorMsg(d.message || 'Change failed'); }
                } catch { setIsLoading(false); setErrorMsg('Service unavailable — try again.'); }
              }}>
                {isLoading ? <Loader2 size={16} style={{ animation: 'lg-spin 1s linear infinite' }}/> : <ShieldCheck size={16}/>}
                {isLoading ? 'Updating Key...' : 'Update Access Key'}
              </button>
              <button onClick={() => { setView('LOGIN'); setErrorMsg(''); setSuccessMsg(''); }} style={{ fontSize: 9, fontWeight: 800, color: '#374151', background: 'transparent', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'color .2s' }} onMouseEnter={e => (e.currentTarget.style.color = '#f59e0b')} onMouseLeave={e => (e.currentTarget.style.color = '#374151')}>
                <ArrowLeft size={11}/>Back to Login
              </button>
            </div>
          )}

          {/* ── OTP VIEW ── */}
          {view === 'OTP' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'lg-fade .4s ease' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(245,158,11,.1)', border: '2px solid rgba(245,158,11,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <Smartphone size={22} style={{ color: '#f59e0b' }}/>
                </div>
                <div style={{ fontSize: 13, fontWeight: 900, color: '#fff', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}>2-Factor Verification</div>
                <div style={{ fontSize: 10, color: '#4b5563', lineHeight: 1.6 }}>Enter the 6-digit OTP sent to your registered contact.</div>
              </div>
              <div className="lg-otp-row" style={{ display: 'flex', justifyContent: 'center' }}>
                {otp.map((v, i) => (
                  <input key={i} ref={el => { otpRefs.current[i] = el; }} value={v} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKey(i, e)} maxLength={1} className="lg-otp-inp" inputMode="numeric"/>
                ))}
              </div>
              <button onClick={verifyOtp} disabled={isLoading} className="lg-btn lg-btn-gold">
                {isLoading ? <Loader2 size={16} style={{ animation: 'lg-spin 1s linear infinite' }}/> : <ShieldCheck size={16}/>}
                {isLoading ? 'Verifying OTP...' : 'Verify & Enter System'}
              </button>
              <button onClick={() => { setView('LOGIN'); setOtp(['','','','','','']); clearError(); }} style={{ fontSize: 9, fontWeight: 800, color: '#374151', background: 'transparent', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'color .2s' }} onMouseEnter={e => (e.currentTarget.style.color = '#f59e0b')} onMouseLeave={e => (e.currentTarget.style.color = '#374151')}>
                <ArrowLeft size={11}/>Back to Login
              </button>
            </div>
          )}

          {/* ── FORGOT PASSWORD VIEW ── */}
          {view === 'FORGOT' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, animation: 'lg-slide .4s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: 4 }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <KeyRound size={22} style={{ color: '#f59e0b' }}/>
                </div>
                <div style={{ fontSize: 13, fontWeight: 900, color: '#fff', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}>System Override</div>
                <div style={{ fontSize: 10, color: '#4b5563', lineHeight: 1.6 }}>Transmit secure reset link to the master admin channel.</div>
              </div>
              <div style={{ position: 'relative' }}>
                <User size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4b5563' }}/>
                <input type="text" value={resetId} onChange={e => setResetId(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReset()} placeholder="Enter System ID or Email" className="lg-inp" autoFocus/>
              </div>
              <button onClick={handleReset} disabled={isLoading || successMsg !== ''} className="lg-btn lg-btn-gold">
                {isLoading ? <Loader2 size={16} style={{ animation: 'lg-spin 1s linear infinite' }}/> : <Mail size={16}/>}
                {isLoading ? 'Encrypting Request...' : 'Transmit Reset Link'}
              </button>
              <button onClick={() => { setView('LOGIN'); setErrorMsg(''); setSuccessMsg(''); }} style={{ fontSize: 9, fontWeight: 800, color: '#374151', background: 'transparent', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'color .2s' }} onMouseEnter={e => (e.currentTarget.style.color = '#f59e0b')} onMouseLeave={e => (e.currentTarget.style.color = '#374151')}>
                <ArrowLeft size={11}/>Abort Override
              </button>
            </div>
          )}

          {/* Card footer */}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <ShieldCheck size={11} style={{ color: 'rgba(245,158,11,.4)' }}/>
            <span style={{ fontSize: 8, fontWeight: 700, color: '#1f2937', letterSpacing: 2, textTransform: 'uppercase' }}>Core Protected by Mamta AI Security V3.1</span>
          </div>
        </div>

        {/* RIGHT — Security badges (desktop) */}
        <div style={{ display: 'none', flexDirection: 'column', gap: 10, marginLeft: 48 }} className="lg-right">
          {[
            { icon: '🔒', l: 'AES-256', sub: 'Encryption' },
            { icon: '🛡️', l: '2FA Ready', sub: 'Auth Layer' },
            { icon: '📡', l: 'Real-Time', sub: 'Monitoring' },
            { icon: '🚀', l: 'AI Powered', sub: 'Mamta V9.5' },
          ].map(b => (
            <div key={b.l} style={{ background: 'rgba(0,0,0,.4)', border: '1px solid rgba(255,255,255,.05)', borderRadius: 10, padding: '10px 14px', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>{b.icon}</span>
              <div><div style={{ fontSize: 9.5, fontWeight: 800, color: '#e2e8f0' }}>{b.l}</div><div style={{ fontSize: 8, color: '#374151', letterSpacing: 1, textTransform: 'uppercase' }}>{b.sub}</div></div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── BOTTOM BAR ─── */}
      <div style={{ position: 'relative', zIndex: 10, padding: '8px 24px', borderTop: '1px solid rgba(255,255,255,.04)', background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 8, color: '#1f2937', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>© 2026 Jaiswal Capital · Mamta AI Pro V9.5 · All Rights Reserved</span>
        <div className="lg-bottom-links" style={{ display: 'flex', gap: 16 }}>
          {['Prop Trading System', 'NSE · BSE · MCX', 'SEBI Compliant'].map(t => (
            <span key={t} style={{ fontSize: 8, color: '#1f2937', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Login;